import React, { createContext, useContext, useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { ImpersonationContext } from "@/contexts/ImpersonationContext";

export type AppRole =
  | "superadmin" | "principal" | "deputy_principal"
  | "head_admission" | "head_administration" | "head_academic"
  | "head_examination" | "head_accounting" | "head_student_service"
  | "head_discipline" | "hod_cmt" | "hod_pst" | "hod_sw"
  | "it_incharge" | "student"
  | "security_man" | "cr_cmt4" | "cr_cmt5" | "cr_cmt6"
  | "cr_pst4" | "cr_pst5" | "cr_pst6"
  | "cr_sw4" | "cr_sw5" | "cr_sw6"
  | "auditor";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  roles: AppRole[];
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRoles = async (userId: string) => {
    try {
      const { data } = await supabase.rpc("get_user_roles", { _user_id: userId });
      if (data) setRoles(data as AppRole[]);
    } catch {
      setRoles([]);
    }
  };

  useEffect(() => {
    let isMounted = true;
    let initialised = false;

    // onAuthStateChange: fire-and-forget for role fetching to avoid deadlocks
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!isMounted) return;
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          // Fire-and-forget: don't block the callback
          fetchRoles(session.user.id).then(() => {
            if (isMounted && !initialised) {
              initialised = true;
              setLoading(false);
            }
          });
        } else {
          setRoles([]);
          if (isMounted && !initialised) {
            initialised = true;
            setLoading(false);
          }
        }
      }
    );

    // getSession handles initial restore + expiry check
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!isMounted) return;

      // Client-side session expiry fast-path
      if (session?.expires_at) {
        const expiresMs = session.expires_at * 1000;
        if (Date.now() >= expiresMs) {
          console.warn("Session expired on restore, signing out");
          await supabase.auth.signOut();
          setSession(null);
          setUser(null);
          setRoles([]);
          if (isMounted && !initialised) { initialised = true; setLoading(false); }
          return;
        }
      }

      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        await fetchRoles(session.user.id);
      }
      if (isMounted && !initialised) {
        initialised = true;
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName }, emailRedirectTo: window.location.origin },
    });
    if (error) throw error;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setRoles([]);
  };

  return (
    <AuthContext.Provider value={{ user, session, roles, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");

  // Check if impersonation context is available (it's a child provider)
  const imp = useContext(ImpersonationContext);

  const isImpersonating = imp?.active ?? false;
  const effectiveRoles: AppRole[] = isImpersonating && imp?.previewRoles ? imp.previewRoles : context.roles;

  return {
    ...context,
    roles: effectiveRoles,
    realRoles: context.roles,
    hasRole: (role: AppRole) => effectiveRoles.includes(role),
    isTopManagement: () =>
      effectiveRoles.includes("superadmin") || effectiveRoles.includes("principal") || effectiveRoles.includes("deputy_principal") || effectiveRoles.includes("auditor"),
    isDepartmentHead: () => effectiveRoles.some((r) => r.startsWith("head_")),
    isImpersonating,
  };
};
